import React, { useMemo, useRef, useEffect } from 'react';
import {
  Editor,
  EditorProvider,
  Toolbar,
  Separator,
} from 'react-simple-wysiwyg';
import {
  BtnBold,
  BtnItalic,
  BtnLink,
  BtnBulletList,
  BtnNumberList,
  createImageButton,
} from '../profiles/Buttons';
import { useAuth0 } from '@auth0/auth0-react';
import { useImageSelection } from './useImageSelection';
import { ImageResizeOverlay } from './ImageResizeOverlay';
import './wysiwyg-editor.scss';

interface WysiwygEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  questionPaste?: boolean;
}

const WysiwygEditor: React.FC<WysiwygEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text...',
  className = '',
  disabled = false,
  questionPaste = false,
}) => {
  //
  const { getAccessTokenSilently } = useAuth0();
  const editorRef = useRef<HTMLDivElement>(null);
  const { selectedImage, setSelectedImage } = useImageSelection(editorRef);

  // Create the image button with access to getAccessTokenSilently
  const BtnImage = useMemo(
    () => createImageButton(getAccessTokenSilently),
    [getAccessTokenSilently],
  );

  const handleChange = (e: any) => {
    onChange(e.target.value);
  };

  // Detect and handle Word/Outlook formatting on paste
  useEffect(() => {
    const editorElement = editorRef.current?.querySelector(
      '[contenteditable="true"]',
    );

    if (!editorElement) return;

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault(); // Always intercept to format properly

      const html = e.clipboardData?.getData('text/html');
      const plainText = e.clipboardData?.getData('text/plain') || '';

      // If questionPaste is enabled, show warning first
      if (questionPaste) {
        const shouldContinue = window.confirm(
          'Pasting content from one email to another can lead to unexpected outcomes.\n\n' +
            "It's better to build the email from scratch here or ask your Dreamie.\n\n" +
            '• Click OK to continue pasting\n' +
            '• Click Cancel to paste as plain text only',
        );

        if (!shouldContinue) {
          // User clicked Cancel - paste as plain text only
          const paragraphs = plainText.split('\n\n').filter((p) => p.trim());
          const formattedHtml = paragraphs
            .map((p) => {
              const withBreaks = p.replace(/\n/g, '<br />');
              return `<p style="margin: 0 0 12px 0; padding: 0;">${withBreaks}</p>`;
            })
            .join('');

          document.execCommand('insertHTML', false, formattedHtml);
          return;
        }
      }

      if (!html) {
        // Plain text paste - convert to proper HTML with paragraph spacing
        const paragraphs = plainText.split('\n\n').filter((p) => p.trim());
        const formattedHtml = paragraphs
          .map((p) => {
            const withBreaks = p.replace(/\n/g, '<br />');
            return `<p style="margin: 0 0 12px 0; padding: 0;">${withBreaks}</p>`;
          })
          .join('');

        document.execCommand('insertHTML', false, formattedHtml);
      } else {
        // HTML paste - insert as-is
        const div = document.createElement('div');
        div.innerHTML = html;
        document.execCommand('insertHTML', false, div.innerHTML);
      }
    };

    editorElement.addEventListener('paste', handlePaste as EventListener);

    // Handle Enter key to insert <br> instead of <p> tags (like Gmail)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        document.execCommand('insertLineBreak');
      }
    };

    editorElement.addEventListener('keydown', handleKeyDown as EventListener);

    return () => {
      editorElement.removeEventListener('paste', handlePaste as EventListener);
      editorElement.removeEventListener(
        'keydown',
        handleKeyDown as EventListener,
      );
    };
  }, [editorRef, questionPaste]);

  return (
    <div className={`wysiwyg-editor-wrapper ${className}`} ref={editorRef}>
      <EditorProvider>
        <Editor value={value} onChange={handleChange} disabled={disabled}>
          <Toolbar>
            <BtnBold />
            <BtnItalic />
            <BtnLink />
            <Separator />
            <BtnBulletList />
            <BtnNumberList />
            <Separator />
            <BtnImage />
          </Toolbar>
        </Editor>
      </EditorProvider>

      {selectedImage && (
        <ImageResizeOverlay
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
};

export default WysiwygEditor;
